import React, { useEffect, useMemo, useState } from "react";
import AdminSidebar from "./AdminSidebar";
import {
  getAdminRegistrations,
  getStudents,
  getAdminExams,
  getSchedules,
  updateRegistration,
  updateExam,
  isProctoringRequired,
  isExamDateProctoringOff,
  normalizeDate,
  getStudentId,
  resolveStudentName,
  describeStudentRef,
} from "../api/adminclient";
import AdminTopbar from "./Admintopbar";

const PAGE_SIZE = 10;

/* ---------- date helpers (local time, no UTC shift) ---------- */
const todayISO = () => {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
};

const formatDate = (iso) => {
  if (!iso) return "Not selected";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  const parsed = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(iso);
  return isNaN(parsed)
    ? iso
    : parsed.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

export default function AdminRegistrations() {
  const [registrations, setRegistrations] = useState([]);
  const [students, setStudents] = useState([]);
  const [exams, setExams] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  // table filters
  const [examFilter, setExamFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [proctorFilter, setProctorFilter] = useState("All");
  const [whenFilter, setWhenFilter] = useState("All"); // All | Upcoming | Past
  const [dateFilter, setDateFilter] = useState("");
  const [page, setPage] = useState(1);

  // control panel
  const [mode, setMode] = useState("student"); // "student" | "examDate"
  const [pStudentId, setPStudentId] = useState("");
  const [pRegId, setPRegId] = useState(""); // registration id or "all"
  const [ruleExamId, setRuleExamId] = useState("");
  const [ruleDate, setRuleDate] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([getAdminRegistrations(), getStudents(), getAdminExams(), getSchedules()])
      .then(([reg, stu, exm, sch]) => {
        setRegistrations(reg.data);
        setStudents(stu.data);
        setExams(exm.data);
        setSchedules(sch.data);
      })
      .catch(() => setError("Could not load registrations. Is json-server running on port 5000?"))
      .finally(() => setLoading(false));
  }, []);

  // auto-hide success notice
  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(""), 3500);
    return () => clearTimeout(t);
  }, [notice]);

  /* ---------- lookups ---------- */
  // Real name if the student exists in /users, otherwise "Student #<ref> (not in users)"
  const studentLabel = (rec) => {
    const name = resolveStudentName(students, rec);
    if (name !== "—") return name;
    const ref = getStudentId(rec);
    return ref !== "" && ref != null ? `Student #${ref} (not in users)` : "—";
  };
  const studentLabelById = (id) => studentLabel({ studentId: id });

  const findExam = (id) => exams.find((e) => String(e.id) === String(id));
  const examName = (id) => findExam(id)?.name || "—";
  const findSchedule = (id) => schedules.find((s) => String(s.id) === String(id));

  // Date the student selected (from booked slot), falling back to registration.date
  const regDate = (r) => normalizeDate(findSchedule(r.scheduleId)?.date || r.date);

  const examTime = (r) => {
    const sc = findSchedule(r.scheduleId);
    return sc ? `${sc.startTime}–${sc.endTime}` : "—";
  };

  const today = todayISO();
  const isUpcoming = (r) => r.status !== "Cancelled" && !!regDate(r) && regDate(r) >= today;

  const whenOf = (r) => {
    const d = regDate(r);
    if (!d) return "none";
    if (d === today) return "today";
    return d > today ? "upcoming" : "past";
  };

  const proctoredOf = (r) => isProctoringRequired(findExam(r.examId), r, regDate(r));

  /* ---------- stats ---------- */
  const stats = useMemo(() => {
    const upcoming = registrations.filter(isUpcoming);
    return {
      total: registrations.length,
      upcoming: upcoming.length,
      proctored: upcoming.filter(proctoredOf).length,
      off: upcoming.filter((r) => !proctoredOf(r)).length,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registrations, exams, schedules]);

  /* ---------- table data ---------- */
  const filtered = useMemo(() => {
    return registrations.filter((r) => {
      const d = regDate(r);
      const matchesExam = examFilter === "All" || String(r.examId) === examFilter;
      const matchesStatus = statusFilter === "All" || r.status === statusFilter;
      const matchesDate = !dateFilter || d === dateFilter;
      const matchesWhen =
        whenFilter === "All" ||
        (whenFilter === "Upcoming" && isUpcoming(r)) ||
        (whenFilter === "Past" && !!d && d < today);
      const on = proctoredOf(r);
      const matchesProctor =
        proctorFilter === "All" || (proctorFilter === "On" ? on : !on);
      return matchesExam && matchesStatus && matchesDate && matchesWhen && matchesProctor;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registrations, exams, schedules, examFilter, statusFilter, dateFilter, whenFilter, proctorFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  /* ---------- actions ---------- */
  const setStatus = async (reg, status) => {
    try {
      await updateRegistration(reg.id, { status });
      setRegistrations((rs) => rs.map((r) => (r.id === reg.id ? { ...r, status } : r)));
    } catch {
      setError("Could not update registration.");
    }
  };

  // Turn proctoring off/on for one or more registrations (student + exam + date)
  const setProctoring = async (regs, disabled) => {
    setError("");
    setBusy(true);
    try {
      await Promise.all(regs.map((r) => updateRegistration(r.id, { proctoringDisabled: disabled })));
      const ids = new Set(regs.map((r) => r.id));
      setRegistrations((rs) => rs.map((r) => (ids.has(r.id) ? { ...r, proctoringDisabled: disabled } : r)));
      return true;
    } catch {
      setError("Could not update the proctoring setting.");
      return false;
    } finally {
      setBusy(false);
    }
  };

  /* ----- Student + Exam panel ----- */
  // Built from the registrations themselves, so a student shows up here even if
  // their record is missing from /users (they then get a "not in users" label).
  const studentsWithUpcoming = useMemo(() => {
    const seen = new Map();
    registrations.filter(isUpcoming).forEach((r) => {
      const id = String(getStudentId(r));
      if (!id || seen.has(id)) return;
      seen.set(id, studentLabel(r));
    });
    return Array.from(seen, ([id, label]) => ({ id, label }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registrations, students, schedules]);

  const studentUpcomingRegs = useMemo(() => {
    if (!pStudentId) return [];
    return registrations
      .filter((r) => String(getStudentId(r)) === String(pStudentId) && isUpcoming(r))
      .sort((a, b) => regDate(a).localeCompare(regDate(b)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registrations, schedules, pStudentId]);

  const targetRegs =
    pRegId === "all"
      ? studentUpcomingRegs
      : studentUpcomingRegs.filter((r) => String(r.id) === String(pRegId));

  const applyStudentRule = async (disabled) => {
    if (!pStudentId) return setError("Select a student first.");
    if (!pRegId) return setError("Select an upcoming exam (or All upcoming exams).");
    if (targetRegs.length === 0) return setError("No matching upcoming exams for this student.");
    const ok = await setProctoring(targetRegs, disabled);
    if (ok) {
      setNotice(
        `Proctoring ${disabled ? "disabled" : "enabled"} for ${studentLabelById(pStudentId)} on ${
          targetRegs.length
        } upcoming exam${targetRegs.length > 1 ? "s" : ""}.`
      );
    }
  };

  /* ----- Exam + Date panel ----- */
  const saveOffDates = async (exam, offDates) => {
    await updateExam(exam.id, { proctoringOffDates: offDates });
    setExams((es) => es.map((e) => (e.id === exam.id ? { ...e, proctoringOffDates: offDates } : e)));
  };

  const disableExamDate = async () => {
    setError("");
    const exam = findExam(ruleExamId);
    if (!exam) return setError("Select an exam first.");
    if (!ruleDate) return setError("Select a date first.");
    const current = Array.isArray(exam.proctoringOffDates) ? exam.proctoringOffDates : [];
    if (current.includes(ruleDate))
      return setError("Proctoring is already disabled for that exam on that date.");
    setBusy(true);
    try {
      await saveOffDates(exam, [...current, ruleDate].sort());
      setRuleDate("");
      setNotice(`Proctoring disabled for ${exam.name} on ${formatDate(ruleDate)}.`);
    } catch {
      setError("Could not disable proctoring for that exam and date.");
    } finally {
      setBusy(false);
    }
  };

  const enableExamDate = async (exam, date) => {
    setError("");
    try {
      const current = Array.isArray(exam.proctoringOffDates) ? exam.proctoringOffDates : [];
      await saveOffDates(exam, current.filter((d) => d !== date));
      setNotice(`Proctoring re-enabled for ${exam.name} on ${formatDate(date)}.`);
    } catch {
      setError("Could not re-enable proctoring for that exam and date.");
    }
  };

  const offRules = useMemo(
    () =>
      exams.flatMap((e) =>
        (Array.isArray(e.proctoringOffDates) ? e.proctoringOffDates : []).map((d) => ({ exam: e, date: d }))
      ),
    [exams]
  );

  // Student-level exceptions that are currently in force (upcoming only)
  const studentOffRegs = useMemo(
    () => registrations.filter((r) => isUpcoming(r) && r.proctoringDisabled),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [registrations, schedules]
  );

  const tone = (status) =>
    status === "Scheduled" ? "active" : status === "Payment Pending" ? "pending" : "inactive";

  return (
    <div className="admin-page-full d-flex">
      <AdminSidebar />
      <div className="admin-content">
        <AdminTopbar />

        <div className="mb-3">
          <h2 className="h5 fw-bold mb-0">Registrations</h2>
          <p className="text-muted small mb-0">
            Manage exam bookings and control proctoring per student, exam and date.
          </p>
        </div>

        {error && <div className="alert alert-warning py-2 small">{error}</div>}
        {notice && <div className="reg-notice"><i className="bi bi-check-circle-fill me-2" />{notice}</div>}

        {/* ---------- Stat cards ---------- */}
        <div className="row g-3 mb-3">
          {[
            { label: "Total Registrations", value: stats.total, icon: "bi-clipboard-check" },
            { label: "Upcoming Exams", value: stats.upcoming, icon: "bi-calendar-event" },
            { label: "Upcoming · Proctored", value: stats.proctored, icon: "bi-camera-video" },
            { label: "Upcoming · Proctoring Off", value: stats.off, icon: "bi-camera-video-off" },
          ].map((c) => (
            <div className="col-6 col-lg-3" key={c.label}>
              <div className="admin-stat-card">
                <div className="admin-stat-icon"><i className={`bi ${c.icon}`} /></div>
                <div className="admin-stat-value">{c.value}</div>
                <div className="admin-stat-label">{c.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* ---------- Proctoring control panel ---------- */}
        <div className="admin-card reg-panel mb-3">
          <div className="reg-panel-head">
            <div>
              <h3 className="h6 fw-bold mb-0">Proctoring control</h3>
              <p className="text-muted small mb-0">Choose who the change should apply to.</p>
            </div>
            <div className="reg-seg" role="tablist">
              <button
                type="button"
                className={`reg-seg-btn ${mode === "student" ? "active" : ""}`}
                onClick={() => setMode("student")}
              >
                <i className="bi bi-person-video" /> Student + Exam
              </button>
              <button
                type="button"
                className={`reg-seg-btn ${mode === "examDate" ? "active" : ""}`}
                onClick={() => setMode("examDate")}
              >
                <i className="bi bi-calendar-check" /> Exam + Date
              </button>
            </div>
          </div>

          {mode === "student" ? (
            <div className="reg-panel-body">
              <p className="reg-hint">
                Turns proctoring off for <strong>one student only</strong>, for the upcoming exam you pick.
                Other students in the same exam stay proctored.
              </p>
              <div className="reg-form-row">
                <div className="reg-field">
                  <label>Student</label>
                  <select
                    className="form-select form-select-sm"
                    value={pStudentId}
                    onChange={(e) => { setPStudentId(e.target.value); setPRegId(""); }}
                  >
                    <option value="">Select a student</option>
                    {studentsWithUpcoming.map((s) => (
                      <option key={s.id} value={s.id}>{s.label}</option>
                    ))}
                  </select>
                </div>

                <div className="reg-field reg-field-wide">
                  <label>Upcoming exam</label>
                  <select
                    className="form-select form-select-sm"
                    value={pRegId}
                    onChange={(e) => setPRegId(e.target.value)}
                    disabled={!pStudentId}
                  >
                    <option value="">{pStudentId ? "Select an upcoming exam" : "Select a student first"}</option>
                    {studentUpcomingRegs.length > 1 && (
                      <option value="all">All upcoming exams ({studentUpcomingRegs.length})</option>
                    )}
                    {studentUpcomingRegs.map((r) => (
                      <option key={r.id} value={r.id}>
                        {examName(r.examId)} · {formatDate(regDate(r))} · {examTime(r)} —{" "}
                        {proctoredOf(r) ? "Proctoring On" : "Proctoring Off"}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="reg-actions">
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => applyStudentRule(true)}
                    disabled={busy}
                  >
                    <i className="bi bi-camera-video-off me-1" />
                    {busy ? "Saving..." : "Disable"}
                  </button>
                  <button
                    className="btn btn-sm btn-outline-success"
                    onClick={() => applyStudentRule(false)}
                    disabled={busy}
                  >
                    <i className="bi bi-camera-video me-1" /> Enable
                  </button>
                </div>
              </div>

              {pStudentId && studentUpcomingRegs.length === 0 && (
                <p className="text-muted small mt-2 mb-0">This student has no upcoming exams.</p>
              )}

              {studentOffRegs.length > 0 && (
                <div className="reg-rules">
                  <div className="reg-rules-title">Students with proctoring off (upcoming)</div>
                  <div className="reg-rule-list">
                    {studentOffRegs.map((r) => (
                      <span key={r.id} className="reg-rule-chip">
                        <i className="bi bi-person-fill-slash" />
                        {studentLabel(r)} · {examName(r.examId)} · {formatDate(regDate(r))}
                        <button
                          type="button"
                          title="Re-enable proctoring"
                          onClick={() => setProctoring([r], false)}
                        >
                          <i className="bi bi-x-lg" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="reg-panel-body">
              <p className="reg-hint">
                Turns proctoring off for <strong>every student</strong> booked on the chosen exam on the
                chosen date. Other dates of the same exam stay proctored.
              </p>
              <div className="reg-form-row">
                <div className="reg-field reg-field-wide">
                  <label>Exam</label>
                  <select
                    className="form-select form-select-sm"
                    value={ruleExamId}
                    onChange={(e) => setRuleExamId(e.target.value)}
                  >
                    <option value="">Select an exam</option>
                    {exams.map((e) => (
                      <option key={e.id} value={e.id}>{e.name}</option>
                    ))}
                  </select>
                </div>
                <div className="reg-field">
                  <label>Date</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={ruleDate}
                    min={today}
                    onChange={(e) => setRuleDate(e.target.value)}
                  />
                </div>
                <div className="reg-actions">
                  <button className="btn btn-sm btn-outline-danger" onClick={disableExamDate} disabled={busy}>
                    <i className="bi bi-camera-video-off me-1" />
                    {busy ? "Saving..." : "Disable proctoring"}
                  </button>
                </div>
              </div>

              {offRules.length > 0 && (
                <div className="reg-rules">
                  <div className="reg-rules-title">Proctoring currently disabled for</div>
                  <div className="reg-rule-list">
                    {offRules.map(({ exam, date }) => (
                      <span key={`${exam.id}-${date}`} className="reg-rule-chip">
                        <i className="bi bi-calendar-x" />
                        {exam.name} · {formatDate(date)}
                        <button
                          type="button"
                          title="Re-enable proctoring"
                          onClick={() => enableExamDate(exam, date)}
                        >
                          <i className="bi bi-x-lg" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ---------- Registrations table ---------- */}
        <div className="admin-card p-3 p-md-4">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
            <h3 className="h6 fw-bold mb-0">
              All Registrations <span className="adm-count ms-1">{filtered.length}</span>
            </h3>
            <div className="d-flex flex-wrap gap-2">
              <select
                className="form-select form-select-sm w-auto"
                value={examFilter}
                onChange={(e) => { setExamFilter(e.target.value); setPage(1); }}
              >
                <option value="All">All Exams</option>
                {exams.map((e) => (
                  <option key={e.id} value={e.id}>{e.name}</option>
                ))}
              </select>
              <select
                className="form-select form-select-sm w-auto"
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              >
                <option value="All">All Status</option>
                <option value="Scheduled">Scheduled</option>
                <option value="Payment Pending">Payment Pending</option>
                <option value="Cancelled">Cancelled</option>
              </select>
              <select
                className="form-select form-select-sm w-auto"
                value={whenFilter}
                onChange={(e) => { setWhenFilter(e.target.value); setPage(1); }}
              >
                <option value="All">All Dates</option>
                <option value="Upcoming">Upcoming</option>
                <option value="Past">Past</option>
              </select>
              <select
                className="form-select form-select-sm w-auto"
                value={proctorFilter}
                onChange={(e) => { setProctorFilter(e.target.value); setPage(1); }}
              >
                <option value="All">Proctoring: All</option>
                <option value="On">Proctoring: On</option>
                <option value="Off">Proctoring: Off</option>
              </select>
              <input
                type="date"
                className="form-control form-control-sm w-auto"
                value={dateFilter}
                title="Filter by exam date"
                onChange={(e) => { setDateFilter(e.target.value); setPage(1); }}
              />
              {dateFilter && (
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => { setDateFilter(""); setPage(1); }}
                >
                  Clear date
                </button>
              )}
            </div>
          </div>

          {loading && <p className="text-muted mb-0">Loading registrations...</p>}
          {!loading && pageItems.length === 0 && (
            <p className="text-muted small mb-0">No registrations match your filters.</p>
          )}

          {!loading && pageItems.length > 0 && (
            <div className="table-responsive">
              <table className="table align-middle admin-table mb-0">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Student</th>
                    <th>Exam</th>
                    <th>Exam Date</th>
                    <th>Time</th>
                    <th>Payment</th>
                    <th>Status</th>
                    <th>Proctoring</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pageItems.map((r, i) => {
                    const exam = findExam(r.examId);
                    const date = regDate(r);
                    const when = whenOf(r);
                    const proctored = isProctoringRequired(exam, r, date);
                    const examOff = exam?.proctoringEnabled === false;
                    const dateOff = isExamDateProctoringOff(exam, date);
                    const locked = examOff || dateOff;
                    const name = resolveStudentName(students, r);

                    const reason = examOff
                      ? "Exam-wide"
                      : dateOff
                      ? "Date rule"
                      : r.proctoringDisabled
                      ? "Student off"
                      : "";

                    const title = examOff
                      ? "Proctoring is disabled for this whole exam (change it in Exams)"
                      : dateOff
                      ? `Proctoring is disabled for this exam on ${formatDate(date)} (remove it in the control panel)`
                      : proctored
                      ? "Click to turn proctoring off for this student"
                      : "Click to turn proctoring back on for this student";

                    return (
                      <tr key={r.id}>
                        <td className="text-muted">{(safePage - 1) * PAGE_SIZE + i + 1}</td>
                        <td className="fw-semibold">
                          {name !== "—" ? (
                            name
                          ) : (
                            <span className="text-danger small fw-normal">
                              — (ref: {describeStudentRef(r)})
                            </span>
                          )}
                        </td>
                        <td>{examName(r.examId)}</td>
                        <td>
                          <div className="reg-date">
                            <span className={date ? "" : "text-muted"}>{formatDate(date)}</span>
                            {when === "today" && <span className="reg-tag reg-tag-today">Today</span>}
                            {when === "upcoming" && <span className="reg-tag reg-tag-upcoming">Upcoming</span>}
                            {when === "past" && <span className="reg-tag reg-tag-past">Past</span>}
                          </div>
                        </td>
                        <td className="text-muted small">{examTime(r)}</td>
                        <td>
                          <span className={`admin-status-pill ${r.paymentStatus === "Paid" ? "active" : "pending"}`}>
                            {r.paymentStatus}
                          </span>
                        </td>
                        <td>
                          <span className={`admin-status-pill ${tone(r.status)}`}>{r.status}</span>
                        </td>
                        <td>
                          <div className="reg-proctor-cell">
                            <button
                              type="button"
                              className={`reg-switch ${proctored ? "on" : "off"}`}
                              disabled={locked || busy}
                              title={title}
                              aria-pressed={proctored}
                              onClick={() => setProctoring([r], !r.proctoringDisabled)}
                            >
                              <span className="reg-switch-knob" />
                              <span className="reg-switch-label">{proctored ? "On" : "Off"}</span>
                            </button>
                            {reason && (
                              <span className="reg-reason">
                                {locked && <i className="bi bi-lock-fill" />} {reason}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="text-end">
                          {r.status !== "Scheduled" && (
                            <button
                              className="btn btn-sm btn-outline-success me-1"
                              title="Mark scheduled"
                              onClick={() => setStatus(r, "Scheduled")}
                            >
                              <i className="bi bi-check-lg" />
                            </button>
                          )}
                          {r.status !== "Cancelled" && (
                            <button
                              className="btn btn-sm btn-outline-danger"
                              title="Cancel registration"
                              onClick={() => setStatus(r, "Cancelled")}
                            >
                              <i className="bi bi-x-lg" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {!loading && totalPages > 1 && (
            <div className="d-flex justify-content-center align-items-center gap-2 mt-3">
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={safePage === 1}
                onClick={() => setPage(safePage - 1)}
              >
                <i className="bi bi-chevron-left" />
              </button>
              <span className="small text-muted">{safePage} / {totalPages}</span>
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={safePage === totalPages}
                onClick={() => setPage(safePage + 1)}
              >
                <i className="bi bi-chevron-right" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}